import type { PageType } from "@/app/hooks/use-app-state";
import { getClientBootstrapRoute } from "@/app/utils/client-bootstrap-route";

/** Keep in sync with `PageType` in use-app-state.ts */
const VALID_PAGE_TYPES = new Set<string>([
  "home",
  "login",
  "notifications",
  "addresses",
  "my-ads",
  "favorites",
  "ad-detail",
  "seller-profile",
  "profile",
  "edit-profile",
  "cart",
  "checkout-address",
  "checkout-shipping",
  "checkout-payment",
  "checkout-installments",
  "checkout-review",
  "checkout-stripe",
  "checkout-second-party",
  "order-confirmation",
  "order-detail",
  "chat",
  "chat-conversation",
  "category-results",
  "settings",
  "help",
  "troca-via-hub",
  "onboarding",
  "wallet",
  "password-security",
  "payment-methods",
  "withdraw",
  "language-region",
  "privacy",
  "data-usage",
  "about",
  "terms",
  "privacy-policy",
  "account-deletion",
  "make-offer",
  "received-offers",
  "sent-offers",
  "sales",
  "purchases",
]);

export const SESSION_APP_ROUTE_KEY = "ezyhub_session_route_v1";

export type SessionAppRoutePersisted = {
  v: 1;
  currentPage: PageType;
  navigationHistory: PageType[];
  searchQuery: string;
  selectedCategory: string;
  selectedAdId: string | null;
  selectedSellerId: string | null;
  selectedSellerName: string | null;
  selectedConversationId: string | null;
  selectedOrderId: string | null;
  selectedOfferId: string | null;
  orderDetailReturnPage: "purchases" | "sales";
  lastOrderNumber: string;
  returnToPage: PageType | null;
};

function isPageType(s: unknown): s is PageType {
  return typeof s === "string" && VALID_PAGE_TYPES.has(s);
}

function parseHistory(raw: unknown): PageType[] {
  if (!Array.isArray(raw)) return [];
  const out: PageType[] = [];
  for (const x of raw) {
    if (isPageType(x)) out.push(x);
    if (out.length >= 30) break;
  }
  return out;
}

function strOr(raw: unknown, fallback: string): string {
  return typeof raw === "string" ? raw : fallback;
}

function strOrNull(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t.length ? t : null;
}

const POST_EMAIL_VERIFY_QUERY = "after_email_verify";
const POST_PASSWORD_RESET_QUERY = "after_password_reset";

function hasAuthLandingLoginQuery(): boolean {
  try {
    const q = new URLSearchParams(window.location.search);
    return q.get(POST_EMAIL_VERIFY_QUERY) === "1" || q.get(POST_PASSWORD_RESET_QUERY) === "1";
  } catch {
    return false;
  }
}

/** Strip cold-start login query params after App consumed them. */
export function removeAuthLandingQueriesFromAddressBar(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const had =
      url.searchParams.get(POST_EMAIL_VERIFY_QUERY) === "1" ||
      url.searchParams.get(POST_PASSWORD_RESET_QUERY) === "1";
    if (!had) return;
    url.searchParams.delete(POST_EMAIL_VERIFY_QUERY);
    url.searchParams.delete(POST_PASSWORD_RESET_QUERY);
    const q = url.searchParams.toString();
    window.history.replaceState(window.history.state, "", url.pathname + (q ? `?${q}` : "") + url.hash);
  } catch {
    /* ignore */
  }
}

/** Remove saved route (e.g. on logout). */
export function clearSessionAppRoute(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_APP_ROUTE_KEY);
  } catch {
    /* ignore */
  }
}

export function persistSessionAppRoute(payload: SessionAppRoutePersisted): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_APP_ROUTE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Restore last in-app screen after a full browser reload.
 * URL cold-start (?ad=, stripe_return) always wins via `getInitialAppRouteState`.
 */
export function readSessionAppRoute(): Omit<SessionAppRoutePersisted, "v"> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_APP_ROUTE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (o.v !== 1) return null;
    if (!isPageType(o.currentPage)) return null;
    return {
      currentPage: o.currentPage,
      navigationHistory: parseHistory(o.navigationHistory),
      searchQuery: strOr(o.searchQuery, ""),
      selectedCategory: strOr(o.selectedCategory, "Todos"),
      selectedAdId: strOrNull(o.selectedAdId),
      selectedSellerId: strOrNull(o.selectedSellerId),
      selectedSellerName: strOrNull(o.selectedSellerName),
      selectedConversationId: strOrNull(o.selectedConversationId),
      selectedOrderId: strOrNull(o.selectedOrderId),
      selectedOfferId: strOrNull(o.selectedOfferId),
      orderDetailReturnPage: o.orderDetailReturnPage === "sales" ? "sales" : "purchases",
      lastOrderNumber: strOr(o.lastOrderNumber, ""),
      returnToPage: isPageType(o.returnToPage) ? o.returnToPage : null,
    };
  } catch {
    return null;
  }
}

export type InitialAppRouteState = {
  currentPage: PageType;
  navigationHistory: PageType[];
  searchQuery: string;
  selectedCategory: string;
  selectedAdId: string | null;
  selectedSellerId: string | null;
  selectedSellerName: string | null;
  selectedConversationId: string | null;
  selectedOrderId: string | null;
  selectedOfferId: string | null;
  orderDetailReturnPage: "purchases" | "sales";
  lastOrderNumber: string;
  returnToPage: PageType | null;
};

const DEFAULT_ROUTE: InitialAppRouteState = {
  currentPage: "home",
  navigationHistory: [],
  searchQuery: "",
  selectedCategory: "Todos",
  selectedAdId: null,
  selectedSellerId: null,
  selectedSellerName: null,
  selectedConversationId: null,
  selectedOrderId: null,
  selectedOfferId: null,
  orderDetailReturnPage: "purchases",
  lastOrderNumber: "",
  returnToPage: null,
};

/** Single source for first paint after reload: URL beats sessionStorage. */
export function getInitialAppRouteState(): InitialAppRouteState {
  if (typeof window !== "undefined") {
    const p = (window.location.pathname || "").replace(/\/+$/, "") || "/";
    if (p === "/conta/exclusao" || p === "/suporte/exclusao-conta") {
      return {
        ...DEFAULT_ROUTE,
        currentPage: "account-deletion",
      };
    }
    if (p === "/" && hasAuthLandingLoginQuery()) {
      return { ...DEFAULT_ROUTE, currentPage: "login" };
    }
  }
  const boot = getClientBootstrapRoute();
  if (boot.currentPage === "order-confirmation" && boot.lastOrderNumber.trim()) {
    return {
      ...DEFAULT_ROUTE,
      currentPage: "order-confirmation",
      lastOrderNumber: boot.lastOrderNumber.trim(),
    };
  }
  if (boot.currentPage === "ad-detail" && boot.selectedAdId) {
    return {
      ...DEFAULT_ROUTE,
      currentPage: "ad-detail",
      selectedAdId: boot.selectedAdId,
    };
  }
  if (boot.currentPage === "received-offers") {
    return {
      ...DEFAULT_ROUTE,
      currentPage: "received-offers",
      selectedOfferId: boot.selectedOfferId,
    };
  }
  const sess = readSessionAppRoute();
  if (!sess) return { ...DEFAULT_ROUTE };
  return {
    ...DEFAULT_ROUTE,
    ...sess,
    selectedOfferId: sess.selectedOfferId ?? DEFAULT_ROUTE.selectedOfferId,
  };
}
